import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { invalidateAfterIdentityMerge } from '@/features/face-matching/hooks/useFaceMatching'
import { casesApi } from '@/services/api/cases'
import { auditApi } from '@/services/api/entity-resolution-audit'
import { schemasApi } from '@/services/api/schemas'

export const AUDIT_PAGE_SIZE = 50

export const auditKeys = {
  list: (caseId: string) => ['case', caseId, 'entity-resolution', 'audit'] as const,
  /** One page of the list; prefixed by `list` so invalidating the list drops every page. */
  page: (caseId: string, page: number, search: string) =>
    [...auditKeys.list(caseId), page, search] as const,
  detail: (caseId: string, auditId: string) =>
    ['case', caseId, 'entity-resolution', 'audit', auditId] as const,
}

export function useAuditLog(caseId: string, page: number, search = '') {
  return useQuery({
    queryKey: auditKeys.page(caseId, page, search),
    queryFn: () =>
      auditApi.list(caseId, {
        limit: AUDIT_PAGE_SIZE,
        offset: page * AUDIT_PAGE_SIZE,
        search: search || undefined,
      }),
    enabled: Boolean(caseId),
    placeholderData: keepPreviousData, // keep the current page visible while the next loads
  })
}

export function useAuditRecord(caseId: string, auditId: string | null) {
  return useQuery({
    queryKey: auditKeys.detail(caseId, auditId ?? ''),
    queryFn: async () => {
      const record = await auditApi.get(caseId, auditId as string)
      if (record.match_basis?.length) return record
      try {
        const settings = await casesApi.getCaseSettings(caseId)
        const schema = settings.schema_id
          ? await schemasApi.get(settings.schema_id)
          : await schemasApi.getActive()
        const unique_property_hints = schema.entity_types
          .filter(type => record.merged_node_labels.includes(type.label))
          .flatMap(type =>
            type.properties.filter(property => property.unique).map(property => property.name)
          )
        return { ...record, unique_property_hints }
      } catch {
        return record
      }
    },
    enabled: Boolean(caseId && auditId),
  })
}

export function useRevertMerge(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (auditId: string) => auditApi.revert(caseId, auditId),
    onSuccess: result => {
      // An undo moves back what the merge moved — faces and their suggestions
      // included (ADR-0042) — so it refreshes the same views: the audit and the
      // clusters under the entity-resolution prefix, the graph, face matching,
      // document faces and the identities.
      invalidateAfterIdentityMerge(queryClient, caseId)
      const note =
        result.skipped > 0
          ? ` · ${result.skipped} edge${result.skipped === 1 ? '' : 's'} couldn’t reconnect`
          : ''
      // What the canonical gave back: edges/props it had only absorbed from this
      // victim (kept if another still-merged member also justifies them).
      const cleaned: string[] = []
      if (result.survivor_edges_removed > 0)
        cleaned.push(
          `${result.survivor_edges_removed} edge${result.survivor_edges_removed === 1 ? '' : 's'}`
        )
      if (result.survivor_props_removed > 0)
        cleaned.push(
          `${result.survivor_props_removed} field${result.survivor_props_removed === 1 ? '' : 's'}`
        )
      const cleanupNote = cleaned.length
        ? ` · removed ${cleaned.join(' + ')} from the canonical`
        : ''
      toast.success(`Merge reverted — ${result.reattached} edges restored${note}${cleanupNote}`)
    },
    onError: (err: Error) => toast.error(err.message || 'Revert failed'),
  })
}
